import React, { useEffect, useState, useRef } from 'react';
import { Search, ChevronDown, X, Check } from 'lucide-react';
import { StatusBadge } from '../components/StatusBadge';
function ArpTooltip() {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({
    top: 0,
    left: 0
  });
  const ref = useRef<HTMLDivElement>(null);
  const handleMouseEnter = () => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setPos({
        top: rect.top + window.scrollY - 8,
        left: rect.left + rect.width / 2 + window.scrollX
      });
    }
    setVisible(true);
  };
  return <div ref={ref} className="relative inline-flex items-center gap-0.5 cursor-help" onMouseEnter={handleMouseEnter} onMouseLeave={() => setVisible(false)} data-id="element-195">
      <span className="text-emerald-600" data-id="element-196">A</span>
      <span className="text-slate-400 mx-0.5" data-id="element-197">/</span>
      <span className="text-rose-600" data-id="element-198">R</span>
      <span className="text-slate-400 mx-0.5" data-id="element-199">/</span>
      <span className="text-amber-600" data-id="element-200">P</span>

      {visible && <div className="fixed z-[9999] pointer-events-none" style={{
      top: pos.top,
      left: pos.left,
      transform: 'translate(-50%, -100%)'
    }} data-id="element-201">
          <div className="bg-slate-800 text-white text-xs px-3 py-2 rounded-lg shadow-lg whitespace-nowrap leading-relaxed mb-2" data-id="element-202">
            <span className="text-emerald-400 font-medium" data-id="element-203">Approved</span>
            {' / '}
            <span className="text-rose-400 font-medium" data-id="element-204">Rejected</span>
            {' / '}
            <span className="text-amber-400 font-medium" data-id="element-205">Pending</span>
            <span className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-slate-800" data-id="element-206" />
          </div>
        </div>}
    </div>;
}
export function Checks({
  onSelectCheck
}: {
  onSelectCheck: (id: number) => void;
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [retailerFilters, setRetailerFilters] = useState<string[]>([]);
  const [statusFilters, setStatusFilters] = useState<string[]>([]);
  const [retailerOpen, setRetailerOpen] = useState(false);
  const [statusOpen, setStatusOpen] = useState(false);
  const retailerRef = useRef<HTMLDivElement>(null);
  const statusRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (retailerRef.current && !retailerRef.current.contains(e.target as Node)) setRetailerOpen(false);
      if (statusRef.current && !statusRef.current.contains(e.target as Node)) setStatusOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);
  const checks = [{
    id: 1,
    retailer: 'Dollar General',
    check: '#DG-9982',
    date: 'Feb 18, 2026',
    status: 'not-started',
    deduction: '$48,200',
    approved: 0,
    rejected: 0,
    pending: 5
  }, {
    id: 2,
    retailer: 'Dollar General',
    check: '#DG-8821',
    date: 'Feb 18, 2026',
    status: 'in-progress',
    deduction: '$12,500',
    approved: 12,
    rejected: 8,
    pending: 20
  }, {
    id: 3,
    retailer: 'Walmart',
    check: '#WM-2219',
    date: 'Feb 17, 2026',
    status: 'in-progress',
    deduction: '$31,800',
    approved: 10,
    rejected: 5,
    pending: 20
  }, {
    id: 4,
    retailer: 'Dollar General',
    check: '#DG-1102',
    date: 'Feb 2, 2026',
    status: 'completed',
    deduction: '$8,900',
    approved: 6,
    rejected: 2,
    pending: 0
  }, {
    id: 5,
    retailer: 'Dollar General',
    check: '#DG-5591',
    date: 'Jan 16, 2026',
    status: 'completed',
    deduction: '$5,400',
    approved: 1,
    rejected: 0,
    pending: 0
  }, {
    id: 6,
    retailer: 'Target',
    check: '#TGT-3321',
    date: 'Jan 10, 2026',
    status: 'completed',
    deduction: '$15,200',
    approved: 32,
    rejected: 0,
    pending: 0
  }];
  const retailers = Array.from(new Set(checks.map(c => c.retailer)));
  const statuses = [{
    value: 'not-started',
    label: 'Logistics Review'
  }, {
    value: 'in-progress',
    label: 'Processing'
  }, {
    value: 'completed',
    label: 'Completed'
  }, {
    value: 'review',
    label: 'Review'
  }, {
    value: 'finance-review',
    label: 'Finance Review'
  }];
  const toggleRetailer = (r: string) => setRetailerFilters(prev => prev.includes(r) ? prev.filter(x => x !== r) : [...prev, r]);
  const toggleStatus = (s: string) => setStatusFilters(prev => prev.includes(s) ? prev.filter(x => x !== s) : [...prev, s]);
  const filteredChecks = checks.filter(check => {
    const q = searchQuery.toLowerCase();
    const matchesSearch = !q || check.check.toLowerCase().includes(q) || check.deduction.toLowerCase().includes(q);
    const matchesRetailer = retailerFilters.length === 0 || retailerFilters.includes(check.retailer);
    const matchesStatus = statusFilters.length === 0 || statusFilters.includes(check.status);
    return matchesSearch && matchesRetailer && matchesStatus;
  });
  const statusLabel = (status: string) => status === 'not-started' ? 'Logistics Review' : status === 'in-progress' ? 'Processing' : status === 'finance-review' ? 'Finance Review' : status.charAt(0).toUpperCase() + status.slice(1);
  const retailerButtonLabel = retailerFilters.length === 0 ? 'Retailer' : retailerFilters.length === 1 ? retailerFilters[0] : `${retailerFilters.length} retailers`;
  const statusButtonLabel = statusFilters.length === 0 ? 'Status' : statusFilters.length === 1 ? statusLabel(statusFilters[0]) : `${statusFilters.length} statuses`;
  return <div className="space-y-8" data-id="element-207">
      <div data-id="element-208">
        <h2 className="text-2xl font-bold text-slate-900 tracking-tight" data-id="element-209">
          Checks
        </h2>
        <p className="text-slate-500 mt-1" data-id="element-210">
          View and manage all received checks and their claim statuses.
        </p>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 shadow-sm" data-id="element-211">
        {/* Toolbar */}
        <div className="p-4 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center gap-3" data-id="element-212">
          {/* Search */}
          <div className="relative max-w-xs w-full" data-id="element-213">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-214">
              <Search className="h-4 w-4 text-slate-400" data-id="element-215" />
            </div>
            <input type="text" className="block w-full pl-10 pr-3 py-2 border border-slate-200 rounded-lg bg-slate-50 placeholder-slate-400 focus:outline-none focus:bg-white focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm transition" placeholder="Search by check # or amount…" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-216" />
          </div>

          {/* Retailer multi-select dropdown */}
          <div className="relative" ref={retailerRef} data-id="element-217">
            <button onClick={() => {
            setRetailerOpen(o => !o);
            setStatusOpen(false);
          }} className={`inline-flex items-center gap-2 px-3 py-2 border rounded-lg text-sm font-medium transition ${retailerFilters.length > 0 ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`} data-id="element-218">
              {retailerButtonLabel}
              {retailerFilters.length > 0 ? <X className="h-3.5 w-3.5" onClick={e => {
              e.stopPropagation();
              setRetailerFilters([]);
            }} data-id="element-219" /> : <ChevronDown className="h-3.5 w-3.5 text-slate-400" data-id="element-220" />}
            </button>
            {retailerOpen && <div className="absolute top-full left-0 mt-1 z-20 w-52 bg-white border border-slate-200 rounded-lg shadow-lg py-1" data-id="element-221">
                {retailers.map(r => {
              const selected = retailerFilters.includes(r);
              return <button key={r} onClick={() => toggleRetailer(r)} className="w-full flex items-center gap-3 px-4 py-2 text-sm hover:bg-slate-50 transition text-slate-700" data-id="element-222">
                      <span className={`flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center transition ${selected ? 'bg-blue-600 border-blue-600' : 'border-slate-300'}`} data-id="element-223">
                        {selected && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} data-id="element-224" />}
                      </span>
                      <span className={selected ? 'text-blue-700 font-medium' : ''} data-id="element-225">
                        {r}
                      </span>
                    </button>;
            })}
              </div>}
          </div>

          {/* Status multi-select dropdown */}
          <div className="relative" ref={statusRef} data-id="element-226">
            <button onClick={() => {
            setStatusOpen(o => !o);
            setRetailerOpen(false);
          }} className={`inline-flex items-center gap-2 px-3 py-2 border rounded-lg text-sm font-medium transition ${statusFilters.length > 0 ? 'border-blue-400 bg-blue-50 text-blue-700' : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50'}`} data-id="element-227">
              {statusButtonLabel}
              {statusFilters.length > 0 ? <X className="h-3.5 w-3.5" onClick={e => {
              e.stopPropagation();
              setStatusFilters([]);
            }} data-id="element-228" /> : <ChevronDown className="h-3.5 w-3.5 text-slate-400" data-id="element-229" />}
            </button>
            {statusOpen && <div className="absolute top-full left-0 mt-1 z-20 w-48 bg-white border border-slate-200 rounded-lg shadow-lg py-1" data-id="element-230">
                {statuses.map(s => {
              const selected = statusFilters.includes(s.value);
              return <button key={s.value} onClick={() => toggleStatus(s.value)} className="w-full flex items-center gap-3 px-4 py-2 text-sm hover:bg-slate-50 transition text-slate-700" data-id="element-231">
                      <span className={`flex-shrink-0 w-4 h-4 rounded border flex items-center justify-center transition ${selected ? 'bg-blue-600 border-blue-600' : 'border-slate-300'}`} data-id="element-232">
                        {selected && <Check className="h-2.5 w-2.5 text-white" strokeWidth={3} data-id="element-233" />}
                      </span>
                      <span className={selected ? 'text-blue-700 font-medium' : ''} data-id="element-234">
                        {s.label}
                      </span>
                    </button>;
            })}
              </div>}
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto rounded-b-xl" data-id="element-235">
          <table className="w-full text-sm text-left" data-id="element-236">
            <thead className="bg-slate-50 text-slate-500 font-medium" data-id="element-237">
              <tr data-id="element-238">
                <th className="px-6 py-3" data-id="element-239">Retailer / Check #</th>
                <th className="px-6 py-3" data-id="element-240">Date Received</th>
                <th className="px-6 py-3" data-id="element-241">Deduction Amount</th>
                <th className="px-3 py-3 w-32" data-id="element-242">Status</th>
                <th className="px-4 py-3 w-24 text-slate-500" data-id="element-243">Total Claims</th>
                <th className="px-4 py-3 w-36" data-id="element-244">
                  <ArpTooltip data-id="element-245" />
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-246">
              {filteredChecks.length > 0 ? filteredChecks.map(check => <tr key={check.id} onClick={() => onSelectCheck(check.id)} className="hover:bg-slate-50 transition-colors cursor-pointer" data-id="element-247">
                    <td className="px-6 py-4" data-id="element-248">
                      <div className="font-medium text-slate-900" data-id="element-249">
                        {check.retailer}
                      </div>
                      <div className="text-[11px] text-slate-500 whitespace-nowrap" data-id="element-250">
                        {check.check}
                      </div>
                    </td>
                    <td className="px-6 py-4 text-slate-600" data-id="element-251">{check.date}</td>
                    <td className="px-6 py-4 font-medium text-slate-900" data-id="element-252">
                      {check.deduction}
                    </td>
                    <td className="px-3 py-4 w-32" data-id="element-253">
                      <StatusBadge status={check.status === 'completed' ? 'success' : check.status === 'in-progress' ? 'processing' : check.status === 'not-started' ? 'neutral' : check.status === 'finance-review' ? 'info' : check.status === 'review' ? 'warning' : 'error'} className="text-[10px]" tooltip={check.status === 'not-started' ? 'Waiting on the Logistics Team to confirm which deductions are invalid' : check.status === 'in-progress' ? 'Sent emails to the retailer, waiting for responses' : check.status === 'completed' ? 'Sent emails to the retailer and received responses. Click for details on how many approved or rejected.' : undefined} data-id="element-254">
                        {statusLabel(check.status)}
                      </StatusBadge>
                    </td>
                    <td className="px-4 py-4 w-24 font-semibold text-slate-900" data-id="element-255">
                      {check.approved + check.rejected + check.pending}
                    </td>
                    <td className="px-4 py-4 w-36 font-medium whitespace-nowrap" data-id="element-256">
                      <span className="text-emerald-600" data-id="element-257">{check.approved}</span>
                      <span className="text-slate-300 mx-1" data-id="element-258">/</span>
                      <span className="text-rose-600" data-id="element-259">{check.rejected}</span>
                      <span className="text-slate-300 mx-1" data-id="element-260">/</span>
                      <span className="text-amber-600" data-id="element-261">{check.pending}</span>
                    </td>
                  </tr>) : <tr data-id="element-262">
                  <td colSpan={6} className="px-6 py-8 text-center text-slate-500" data-id="element-263">
                    No checks found matching your filters.
                  </td>
                </tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>;
}